import React, { useRef, useState } from "react";
import Header from "../components/Header";
import { ProviderSelect } from "../components/ProviderSelect";
import { useAuth } from "../context/AuthContext";
import { Gamepad2, Users, Send, Check, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GD_SERVICES = ["concept art", "modeling", "rigging", "animation", "lighting and comp", "deployment"];

const CommonFields = ({ form, set, prefix }) => (
  <>
    <label className="req-field"><span className="req-label">Full name*</span>
      <input className="req-input" value={form.name} onChange={set("name")} required data-testid={`${prefix}-name`} /></label>
    <label className="req-field"><span className="req-label">Number*</span>
      <input className="req-input" type="tel" value={form.phone} onChange={set("phone")} required data-testid={`${prefix}-phone`} /></label>
    <label className="req-field"><span className="req-label">Instagram handle</span>
      <input className="req-input" value={form.instagram} onChange={set("instagram")} placeholder="@yourhandle" data-testid={`${prefix}-instagram`} /></label>
    <label className="req-field"><span className="req-label">Email*</span>
      <input className="req-input" type="email" value={form.email} onChange={set("email")} required data-testid={`${prefix}-email`} /></label>
  </>
);

const useSend = () => {
  const [state, setState] = useState("idle");
  const send = async (body) => {
    setState("busy");
    try {
      const res = await fetch(`${API}/api/services/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setState(res.ok ? "ok" : "error");
    } catch {
      setState("error");
    }
  };
  return [state, send];
};

const HelpForm = () => {
  const { token } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", instagram: "", email: "", blurb: "", provider_id: "" });
  const [services, setServices] = useState([]);
  const [conceptUrl, setConceptUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [state, send] = useSend();

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const toggleSvc = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const uploadConcept = async (file) => {
    if (!file || !token) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/uploads`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      if (res.ok) setConceptUrl(`${API}${data.url}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form className="req-form" data-testid="gamedev-help-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (services.length === 0) return;
        send({ type: "gamedev", ...form, services_needed: services, concept_url: conceptUrl });
      }}>
      <h3 className="req-title">Request game dev help</h3>
      <div className="req-grid">
        <CommonFields form={form} set={set} prefix="gd" />
      </div>
      <div className="req-field">
        <span className="req-label">What do you need?* (pick at least one)</span>
        <div className="gd-services-grid" data-testid="gd-services">
          {GD_SERVICES.map((s) => (
            <label key={s} className={`gd-service-box${services.includes(s) ? " selected" : ""}`}
              data-testid={`gd-service-${s.replace(/ /g, "-")}`}>
              <input type="checkbox" checked={services.includes(s)} onChange={() => toggleSvc(s)} />
              <span>{s}</span>
            </label>
          ))}
        </div>
      </div>
      <label className="req-field"><span className="req-label">Short blurb — what's the game?</span>
        <textarea className="req-input" rows={3} value={form.blurb} onChange={set("blurb")}
          placeholder="Genre, vibe, story, platform…" data-testid="gd-blurb" /></label>
      <div className="req-field">
        <span className="req-label">Upload a concept (image, doc, or video)</span>
        {token ? (
          <div className="gd-upload-row">
            <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()}
              disabled={uploading} data-testid="gd-upload-btn">
              <Upload size={15} /> {uploading ? "Uploading…" : conceptUrl ? "Replace file" : "Choose file"}
            </button>
            {conceptUrl && <span className="gd-upload-ok"><Check size={14} /> attached</span>}
            <input ref={fileRef} type="file" hidden accept="image/*,video/*,.pdf"
              onChange={(e) => uploadConcept(e.target.files?.[0])} />
          </div>
        ) : (
          <p className="gd-upload-note">Log in to attach a file — or describe it in the blurb.</p>
        )}
      </div>
      <ProviderSelect type="gamedev" value={form.provider_id}
        onChange={(v) => setForm({ ...form, provider_id: v })} />
      {state === "ok" ? (
        <div className="shirt-claim-ok" data-testid="gd-help-ok"><Check size={16} /> Request sent — we'll email you.</div>
      ) : (
        <button className="btn-solid" type="submit" disabled={state === "busy" || services.length === 0} data-testid="gd-help-submit">
          <Send size={15} /> {state === "busy" ? "Sending…" : "Send request"}
        </button>
      )}
      {state === "error" && <div className="cart-error">Couldn't send — check the fields and try again.</div>}
    </form>
  );
};

const TeamForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", instagram: "", email: "", role: "", portfolio: "", about: "" });
  const [state, send] = useSend();
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <form className="req-form" data-testid="gamedev-team-form"
      onSubmit={(e) => { e.preventDefault(); send({ type: "gamedevteam", ...form }); }}>
      <h3 className="req-title">Join the GameDev team</h3>
      <div className="req-grid">
        <CommonFields form={form} set={set} prefix="gdt" />
        <label className="req-field"><span className="req-label">What do you do?*</span>
          <input className="req-input" value={form.role} onChange={set("role")} required
            placeholder="Concept art, modeling, code, sound…" data-testid="gdt-role" /></label>
        <label className="req-field"><span className="req-label">Portfolio link</span>
          <input className="req-input" value={form.portfolio} onChange={set("portfolio")}
            placeholder="https://…" data-testid="gdt-portfolio" /></label>
      </div>
      <label className="req-field"><span className="req-label">Why do you want in?*</span>
        <textarea className="req-input" rows={3} value={form.about} onChange={set("about")} required data-testid="gdt-about" /></label>
      {state === "ok" ? (
        <div className="shirt-claim-ok" data-testid="gdt-ok"><Check size={16} /> Application sent — we'll email you.</div>
      ) : (
        <button className="btn-solid" type="submit" disabled={state === "busy"} data-testid="gdt-submit">
          <Send size={15} /> {state === "busy" ? "Sending…" : "Apply"}
        </button>
      )}
      {state === "error" && <div className="cart-error">Couldn't send — check the fields and try again.</div>}
    </form>
  );
};

const Gaming = () => {
  const [open, setOpen] = useState("");
  React.useEffect(() => { document.title = "TMM|GAMING"; }, []);

  return (
    <>
      <Header hoverColor="rgba(255,120,80,1)" compact={true} />
      <div className="services-page" data-testid="gaming-page">
        <header className="services-head">
          <img src="/img/nav_gamedev_hero.png" alt="GameDev" className="services-head-mark gamedev" data-testid="gamedev-head-mark" />
          <p className="fashion-eyebrow">From concept to deployment — let's build your game.</p>
        </header>
        <div className="req-toggle-row services-toggles">
          <button className="btn-ghost" onClick={() => setOpen(open === "help" ? "" : "help")} data-testid="gamedev-help-toggle">
            <Gamepad2 size={16} /> Request game dev help
          </button>
          <button className="btn-ghost" onClick={() => setOpen(open === "team" ? "" : "team")} data-testid="gamedev-team-toggle">
            <Users size={16} /> Join the GameDev team
          </button>
        </div>
        {open === "help" && <HelpForm />}
        {open === "team" && <TeamForm />}
      </div>
    </>
  );
};

export default Gaming;
